// kvd
//   ESRecord allows us to track:
//     what's been written to the database  ( So the mapper knows to Insert/ Update record when changes are persisted.)
//     what is logically deleted on the front end ( So the mapper knows to Destroy record when changes are persisted.)
//
import * as _ from 'lodash'
import { Record, utils } from 'js-data'
import { ESDataStore } from './ESDataStore'
import { ESRecordMapper } from './ESRecordMapper'

// Copied from js-data Schema.js
// These strings are cached for optimal performance of the change detection
// boolean - Whether a Record is changing in the current execution frame
const changingPath = 'changing'
// string[] - Properties that have changed in the current execution frame
const changedPath = 'changed'
// Object[] - History of change records
const changeHistoryPath = 'history'
// boolean - Whether a Record is currently being instantiated
const creatingPath = 'creating'
// number - The setTimeout change event id of a Record, if any
const eventIdPath = 'eventId'
// boolean - Whether to skip validation for a Record's currently changing property
const noValidatePath = 'noValidate'
// boolean - Whether to preserve Change History for a Record
const keepChangeHistoryPath = 'keepChangeHistory'
// boolean - Whether to skip change notification for a Record's currently
// changing property
const silentPath = 'silent'

export interface ESRecordExtensions {
    afterCreateRecordWithKey?: (props: any, opts: any) => Promise<void>
    setDefaults?: () => void
    beforeSave?: (SaveDateTime: Date, opts?: any) => void
}

export interface IHasChangesOptions {
    properties?: string[]
}

export interface IRecordChangeFunction {
    (record: ESRecord, propertyName: string, propertyValue: any, options: any): void
}

export class ESRecord extends Record implements ESRecordExtensions {
    _rowExists: boolean
    _rowDeleted: boolean
    _forceSave: boolean

    constructor(props: any, opts: any) {
        super(props, opts)
        this._rowExists = false
        this._rowDeleted = false
        this._forceSave = false
    }

    get isActive(): boolean {
        if (this._rowDeleted) {
            return false
        }

        let mapper: ESRecordMapper = <any>this._mapper()
        if (mapper.bDeleteIndicatorExists) {
            let recDeleteFieldValue = (<any>this)[mapper.DeletedIndicatorFieldName]
            return recDeleteFieldValue != mapper.DeletedIndicatorDeleteValue
        }

        return true
    }

    get isActiveNew(): boolean {
        return !this._rowExists && this.isActive
    }

    set isActive(value: boolean) {
        // Don't set anything
    }

    markDeleted(): void {
        let mapper: ESRecordMapper = <any>this._mapper()
        if (mapper.bDeleteIndicatorExists) {
            let rec = <any>this
            rec[mapper.DeletedIndicatorFieldName] = mapper.DeletedIndicatorDeleteValue
        } else {
            this._rowDeleted = true
        }

        this.emit('change:isActive', this, false)
        this.emit('change:isActiveNew', this, false)
    }

    forceDelete(): void {
        this._rowDeleted = true
        this.emit('change:isActive', this, false)
        this.emit('change:isActiveNew', this, false)
    }

    forceSave(): void {
        this._forceSave = true
    }

    hasChanges(pOpts?: IHasChangesOptions): boolean {
        let opts: IHasChangesOptions = pOpts || {}
        if (this._forceSave) {
            return true
        }

        // changes() function is expensive, so we need a better/ faster negative case, which is the common case
        let quickChangeInd = this._get('changedInd')
        if (!quickChangeInd) {
            return false
        }

        let esRecordChanged = this.changes().changed
        if (_.isEmpty(esRecordChanged)) {
            return false
        }

        // if the changes are only undefined values, then don't update them..
        let bChanged = false

        if ('properties' in opts) {
            let props: string[] = opts.properties
            _.forEach(props, (aProp) => {
                if (aProp in esRecordChanged && !_.isUndefined(esRecordChanged[aProp])) {
                    bChanged = true
                    return false
                }
            })
        } else {
            _.forEach(esRecordChanged, (value, key) => {
                if (!_.isUndefined(value)) {
                    bChanged = true
                    return false // stop iterating
                }
            })
        }

        return bChanged
    }

    getChangedProperties(pOpts?: IHasChangesOptions): string[] {
        let arr: string[] = []
        let opts: IHasChangesOptions = pOpts || {}

        const esRecordChanged = this.changes().changed
        if (_.isEmpty(esRecordChanged)) {
            return []
        }

        // if the changes are only undefined values, then don't update them..

        if ('properties' in opts) {
            let props: string[] = opts.properties
            _.forEach(props, (aProp) => {
                if (aProp in esRecordChanged && !_.isUndefined(esRecordChanged[aProp])) {
                    arr.push(aProp)
                }
            })
        } else {
            _.forEach(esRecordChanged, (value, key) => {
                if (!_.isUndefined(value)) {
                    arr.push(key)
                }
            })
        }

        return arr
    }

    setManualIndicator() {
        let mapper: ESRecordMapper = <any>this._mapper()
        if (mapper.bManualIndicatorExists) {
            let rec = <any>this
            rec[mapper.ManualIndicatorFieldName] = mapper.ManualIndicatorFieldValue
        }
    }

    dataStore(): ESDataStore {
        return (<any>this._mapper()).datastore
    }

    setupOnChangeEvents(arrProperties: string[], changeFunction: IRecordChangeFunction, opts?: any) {
        const self = this

        if (_.isEmpty(arrProperties)) {
            arrProperties = []
            let prsearch = (val: any, key: string) => {
                if (_.isFunction(val)) {
                    return
                }

                if (key.startsWith('_') || key == 'cif') {
                    return
                }

                arrProperties.push(key)
            }

            _.forIn(this, prsearch)
        }

        // cancelChangeEvents
        self.cancelChangeEvent()

        _.forEach(arrProperties, (key) => {
            self.on(
                'change:' + key,
                function (record: ESRecord, newValue: any) {
                    changeFunction(record, this.propertyName, newValue, this.options)
                },
                { propertyName: key, options: opts }
            )
        })
    }

    cancelChangeEvent() {
        const eventId = this._get('eventId')
        if (eventId) {
            clearTimeout(eventId)
            this._unset(changedPath)
            this._unset(eventIdPath)
            this._unset(changingPath)
            this._unset(silentPath)
        }
    }

    setupDependents(emitPropertyName: string, sourceProperties: string[]) {
        const self = this
        _.forEach(sourceProperties, (srcProp) => {
            self.on('change:' + srcProp, function (record: ESRecord, newValue: any) {
                let emitSrc = emitPropertyName
                self.emit('change:' + emitSrc, self, (<any>record)[emitPropertyName])
            })
        })
    }

    setColumnDefaults() {
        const self: any = this
        let mapper = this._mapper()

        _.forEach(mapper.schema.properties, (value, fieldName) => {
            if (value.type == 'string') {
                self[fieldName] = ''
            } else if (value.type == 'number') {
                self[fieldName] = 0
            }
        })
    }

    get Row_Id(): number {
        const mapper = this._mapper()
        // @ts-expect-error "typings"
        return <number>this[mapper.idAttribute]
    }
}
